import type { ReactNode } from 'react'
import type { ApplicationTerms } from './domain/campaign'

/**
 * Type layers in this file (not physically grouped — use this legend):
 * - V2 wire (mirrors offer-mgt): OfferResponse, OfferRates, CampaignResponse, BundleResponse,
 *   RewardsAttributes, and the *V2 suffixed ones (CampaignTermsResponseV2, ProductDetailsV2, …).
 *   Adapted at the network seam (lib/converters/) into the domain model.
 * - Domain-shared building blocks: OfferProduct, OfferPricing, ProductAttributes, SubChannelContext,
 *   CategoryReward — reused by src/shared/domain/ (Campaign, Offer, ApplicationTerms) and the V2 adapters.
 * - Submission: *Submission* request shapes.
 * Note: OfferPricing (domain: fee+APR+lines) and OfferRates (V2 wire: fee+APR; lines live on
 * decisioningInput) are the same concept at different layers — not duplicates.
 */

export type ApplicantType = 'PRIMARY' | 'SECONDARY'
export type ApplicationType = 'PREQUAL' | 'APPLICATION'
export type SubmissionSource = 'PREQUAL' | 'DIRECT_APPLICATION' | 'CREDIT_KARMA_EASY_APPLY'

export type LegalDocument = {
  documentId: string
  documentType: 'APPLICATION_DISCLOSURE' | 'CARDHOLDER_AGREEMENT'
  documentUri: string
}

export type DocumentContentType = 'application/pdf' | 'text/html'

export type UserDocument = {
  documentId: string
  contentType: DocumentContentType
  uri: string
}

export type SnapshotDocumentType =
  | 'REVIEW_PAGE_HTML'
  | 'DISCLOSURE_HTML'
  | 'APPLICATION_HTML'
  | 'CFU_CONSENT_HTML'
  | 'PREQUAL_INQUIRY_HTML'
  | 'CHOOSE_YOUR_LINE_HTML'
  | 'TAX_DATA_CONSENT_HTML'
  | 'CREDIBLE_DISCLOSURE'

export type SnapshotDocument = {
  type: SnapshotDocumentType
  content: string
}

export const BankAccountType = {
  BOTH: 'BOTH',
  CHECKING: 'CHECKING',
  NEITHER: 'NEITHER',
  SAVINGS: 'SAVINGS',
} as const

export type BankAccountType = (typeof BankAccountType)[keyof typeof BankAccountType]

export const EmploymentStatus = {
  EMPLOYED: 'EMPLOYED',
  SELF_EMPLOYED: 'SELF_EMPLOYED',
  RETIRED: 'RETIRED',
  STUDENT: 'STUDENT',
  UNEMPLOYED: 'UNEMPLOYED',
} as const

export type EmploymentStatus = (typeof EmploymentStatus)[keyof typeof EmploymentStatus]

export const HomeOwnership = {
  NEITHER: 'NEITHER',
  OWN: 'OWN',
  RENT: 'RENT',
} as const

export type HomeOwnership = (typeof HomeOwnership)[keyof typeof HomeOwnership]

export type OfferTermDisplay = {
  id: string
  title: string
  value?: ReactNode
  subValue?: ReactNode
}

export interface MlaTerms {
  annualFee: number
  purchaseApr: number
  cashApr: number
  minimumLine: number
  rewardsRate: number
  maximumLine: number
  monthlyFee?: number
  feeFrequency?: string
}

export type CategoryReward = 'D' | 'G' | 'DG' | 'DT' | 'TDG' | 'DYNAMIC'

export interface SubChannelContextLegacy {
  id: string
  contextName?: string
  channel?: string
  subChannel?: string
  subChannelAttributes?: {
    id: string
    attributeName: string
    description: string
  }[]
}

export const ErrorType = {
  AccountSetupError: 'AccountSetupError',
  ApplicationError: 'ApplicationError',
  CfuGenericError: 'CfuGenericError',
  CraPollingError: 'CraPollingError',
  IdVerificationPollingError: 'IdVerificationPollingError',
  InquiryError: 'InquiryError',
  LongerThanUsualError: 'LongerThanUsualError',
} as const

export type ErrorType = (typeof ErrorType)[keyof typeof ErrorType]

export type ErrorPage = {
  header: string
  content: ReactNode
}

export type LoginFormCaseLookup = {
  [key in LoginCase]: LoginFormInputSet
}

export type LoginFormInputSet = {
  username: string
  password: string
}

export const LoginCase = {
  Eligible: 'Eligible',
  Ineligible: 'Ineligible',
} as const

export type LoginCase = (typeof LoginCase)[keyof typeof LoginCase]

export type FormSubmissionInputSet = {
  firstName: string
  middleInitial: string
  lastName: string
  dateOfBirth: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  zipCode: string
  rentOrMortgage: string
  employmentStatus: EmploymentStatus
  income: string
  password: string
  ssn: string
  email: string
  emailConfirm: string
  phoneNumber: string
  interestedInBlankChecks?: boolean
  addOnProducts?: {
    creditProtectionProducts: CreditProtectionProduct[]
  }
}

export type FormSubmissionCaseLookup = {
  [key in FormSubmissionCase]: Partial<FormSubmissionInputSet>
}

export const FormSubmissionCase = {
  ApplicationApprovedCard: 'ApplicationApprovedCard',
  ApplicationApprovedOptOutCreditProtection: 'ApplicationApprovedOptOutCreditProtection',
  ApplicationDeclinedCard: 'ApplicationDeclinedCard',
  ApplicationNotFound: 'ApplicationNotFound',
  ApplicationServerError: 'ApplicationServerError',
  ProactiveMulticardPasswordSet: 'ProactiveMulticardPasswordSet',
  EFAPhoneVerification: 'EFAPhoneVerification',
  FrozenPausedApp: 'FrozenPausedApp',
  FrozenPausedPrequal: 'FrozenPausedPrequal',
  IDologyApprovedCard: 'IDologyApprovedCard',
  PqApprovedCard: 'PqApprovedCard',
  PqApprovedSpendBonusCard: 'PqApprovedSpendBonusCard',
  PqApprovedTeaserRateCard: 'PqApprovedTeaserRateCard',
  PqApprovedWebBankCard: 'PqApprovedWebBankCard',
  PqApprovedCategoryRewardsCard: 'PqApprovedCategoryRewardsCard',
  PqApprovedChooseYourLine: 'PqApprovedChooseYourLine',
  PqApprovedDynamicRewardsCard: 'PqApprovedDynamicRewardsCard',
  PqApprovedSecuredCard: 'PqApprovedSecuredCard',
  PqCfuApprovedCard: 'PqCfuApprovedCard',
  PqDeclinedCard: 'PqDeclinedCard',
  PqIdConflict: 'PqIdConflict',
  IdConflict: 'IdConflict',
  CfuApprovedCard: 'CfuApprovedCard',
  CfuBankError: 'CfuBankError',
  CfuRequestBankRelogin: 'CfuRequestBankRelogin',
  RequestFunding: 'RequestFunding',
  RequestFundingWithMla: 'RequestFundingWithMla',
  PqRequestIdentityVerification: 'PqRequestIdentityVerification',
  RequestIdentityVerification: 'RequestIdentityVerification',
  RequestIncomeVerification: 'RequestIncomeVerification',
  RequestProofOfIncome: 'RequestProofOfIncome',
  ApplicationApprovedSecuredCard: 'ApplicationApprovedSecuredCard',
  ApplicationApprovedWithMla: 'ApplicationApprovedWithMla',
  CkLegacyRequestIdentityVerification: 'CkLegacyRequestIdentityVerification',
  OfacReview: 'OfacReview',
  PqOfacReview: 'PqOfacReview',
  CounterOffer: 'CounterOffer',
  PqCounterOffer: 'PqCounterOffer',
} as const

export type FormSubmissionCase = (typeof FormSubmissionCase)[keyof typeof FormSubmissionCase]

export type ApplicationStatus =
  'ACCEPTED' | 'APPROVED' | 'DECLINED' | 'EXPIRED' | 'PAUSED' | 'STARTED'

export type ApplicationStatusReason =
  | 'APPROVED'
  | 'BAD_CREDIT_HISTORY'
  | 'COUNTER_OFFER'
  | 'DECISIONING'
  | 'EFA_PHONE_VERIFICATION'
  | 'FROZEN'
  | 'ID_CONFLICT'
  | 'INELIGIBLE'
  | 'MODEL_DECLINE'
  | 'OFAC_REVIEW'
  | 'PREQUAL_APPROVED'
  | 'REQUEST_BANK_LOGIN'
  | 'REQUEST_FUNDING'
  | 'REQUEST_IDENTITY_VERIFICATION'
  | 'REQUEST_IDOLOGY_QUESTIONS'
  | 'REQUEST_INCOME_VERIFICATION'

export interface ApplicantSubmissionAddressResponse {
  addressLineOne: string
  addressLineTwo: string
  city: string
  state: string
  zip: string
}

export interface ApplicantSubmissionContactResponse {
  phoneNumber: string
  emailAddress: string
  address: ApplicantSubmissionAddressResponse
}

export interface ApplicantSubmissionResponse {
  applicantId: string
  applicantType: ApplicantType
  firstName: string
  middleName: string
  lastName: string
  dateOfBirth: string
  contactDetails: ApplicantSubmissionContactResponse
}

export interface PrimaryApplicantSubmissionResponse extends ApplicantSubmissionResponse {
  applicantType: 'PRIMARY'
}

export type ApplicationSubmissionResponse = {
  applicationId: string
}

export interface ApplicationSubmissionData {
  applicants: [PrimaryApplicantSubmissionResponse, ...Array<ApplicantSubmissionResponse>]
  applicationType: ApplicationType
  source: SubmissionSource
  productDetails: ProductDetails
  addOnProducts: AddOnProducts
}

export type ApprovedOutcome = {
  initialCreditLine: string
  activeDuty: boolean
}

export type PendingOutcome = {
  activeDuty: boolean
}

export type AdverseFactor = {
  rank: number
  factor: string
}

export type DeclinedOutcome = {
  declineFactors: AdverseFactor[]
  bureauScoreData: {
    name: string
    score: string
    scoreType: string
    scoreFactors: AdverseFactor[]
  }
  bureauPulled: boolean
  bureauPulledDate: string
}

export type ExpiredOutcome = {
  statusReasonAtExpiration: string
  expireDate: string
  bureauPulled: boolean
  bureauPulledDate: string
}

export type ApplicationSummaryResponse = {
  applicationId: string
  status: ApplicationStatus
  statusReason: ApplicationStatusReason
  outcome?: ApprovedOutcome | DeclinedOutcome | ExpiredOutcome | PendingOutcome
  applicationType: ApplicationType
  submission: ApplicationSubmissionData
  submittedAt: string
}

export interface OutOfWalletQuestion {
  answers: string[]
  type: string
  prompt: string
}

export interface OutOfWalletQuestionSet {
  questions: OutOfWalletQuestion[]
}

type FundingStatus = 'STARTED' | 'PENDING' | 'ERRORED' | 'COMPLETED'

export type FundingErrorCode =
  | 'PROCESSOR_EXPIRED_CARD'
  | 'PROCESSOR_INCORRECT_CVC'
  | 'PROCESSOR_INCORRECT_CARD_NUMBER'
  | 'PROCESSOR_INSUFFICIENT_FUNDS'
  | 'PROCESSOR_TOO_MANY_DECLINES'

export type FundingStatusResponse = {
  referenceId: string
  status: FundingStatus
  attemptCount: number
  depositAmount: number
  redirectUrl?: string | null
  errorCode?: FundingErrorCode
}

export type FrozenStatusResponse = {
  canRepullAt: string
  bureau: 'TRANSUNION'
}

export type IdentityVerificationResponse = {
  referenceId: string // Socure reference ID
  status:
    | 'ACCEPT' // Added for Socure v5 flow
    | 'APP_OPENED'
    | 'DOCUMENT_BACK_UPLOADED'
    | 'DOCUMENT_FRONT_UPLOADED'
    | 'DOCUMENT_SELFIE_UPLOADED'
    | 'DOCUMENTS_UPLOADED'
    | 'NOT_AVAILABLE'
    | 'REJECT' // Added for Socure v5 flow
    | 'RESUBMIT'
    | 'SESSION_EXPIRED'
    | 'SESSION_COMPLETE'
    | 'UNEXPECTED_SERVER_ERROR'
    | 'VERIFICATION_COMPLETED'
    | 'VERIFICATION_ERROR'
    | 'WAITING_FOR_USER_TO_REDIRECT'
}

export const Channel = {
  MailOffer: 'mail_offer',
  Prequal: 'conversation-prequal',
  QR: 'QR_Code',
  DM: 'Direct_Mail',
  CK: 'Credit_Karma',
  BMG: 'BMG',
  Impact: 'Impact',
} as const

export type Channel = (typeof Channel)[keyof typeof Channel]

export const NidPageIdentifier = {
  ApplicationInfo: 'CAFE_APPLICATION_FLOW_INFO',
} as const

export type NidPageIdentifier = (typeof NidPageIdentifier)[keyof typeof NidPageIdentifier]

export interface CreditProtectionProduct {
  name: 'COMPLETE'
  attributes: {
    customerSelection: 'OPT_IN' | 'OPT_OUT' | 'SKIPPED'
  }
}

export type FinancialDocument =
  'BANK_STATEMENT' | 'BENEFITS' | 'PAYSTUB' | 'STATEMENT_FROM_SOURCE' | 'TAX_1099' | 'TAX_W2'

export type QuestionSet = 'prequal' | 'securedCard' | 'proofOfIncome'

type Address = {
  city: string
  state: string
  street1: string
  zip: string
  street2: string
  updatedAt: string
}

export type MulticardCampaign = {
  customer: {
    id: string
    addresses: {
      primaryAddress: Address
      alt1Address: Address | null
    }
    firstName: string
    lastName: string
    middleName: string
    lastFourSsn: string
    ssn: null
    birthDate: string
    contact: {
      email: string
      alt1Phone: string | null
      alt2Phone: string | null
      primaryPhone: string
      emailUpdatedAt: string
      primaryPhoneUpdatedAt: string
    }
  }
  /** List of campaign names. There should only be one per MulticardCampaign response for now. */
  campaigns: string[]
}

export type DocumentInfo = {
  documentType: FinancialDocument
  file: File
}

export type PoiDocumentPayload = {
  applicationId: string
  data: DocumentInfo[]
}

type PasswordSetStatus = 'SET' | 'NOT_SET'

export type IdentityStatusResponse = {
  credentialsStatus: PasswordSetStatus
}

export type CraLinkTokenResponse = {
  token: string | null
  appExpiration: string
}

export type CraSubContext = 'DECISIONING'

export type CraStatus =
  | 'BANK_ERROR'
  | 'COMPLETED'
  | 'ITEM_ERROR'
  | 'MISSING_DEPOSITORY_ACCOUNT_ERROR'
  | 'NOT_FOUND'
  | 'PENDING'
  | 'PLAID_LINK_EXPIRED'

export type CraStatusResponse = {
  reportStatus: CraStatus
}

export type ReferralVendorName = 'CREDIBLE'

export type ReferralVendor = {
  name: ReferralVendorName
}

/** Presence of `vendor` carries eligibility; an empty response means no referral offer */
export type ReferralEligibilityResponse = {
  vendor?: ReferralVendor
}

export type ReferralSubmitPayload = {
  vendor: ReferralVendor
  applicationId: string
}

export type ReferralVendorStatus = 'SUCCESS' | 'ERROR'

/** A vendor failure (including a vendor timeout) returns 200 with `vendorStatus: ERROR` and no `vendorResponse` */
export type ReferralSubmitResponse = {
  vendor: ReferralVendor
  vendorStatus: ReferralVendorStatus
  vendorResponse?: {
    uuid: string
    link: string
  }
}

export type Cookie = Partial<{
  [key: string]: string
}>

export type CardArtAssetName =
  | 'greenlineV0'
  | 'silverlineV0'
  | 'goldlineV0'
  | 'silver-green'
  | 'gold-green'
  | 'silver-gold'
  | 'gold-silver-green'

// https://stackoverflow.com/a/68404823/9072953
type DotPrefix<T extends string> = T extends '' ? '' : `.${T}`

type DotNestedKeys<T> = (
  T extends Array<unknown>
    ? ''
    : T extends object
      ? { [K in Exclude<keyof T, symbol>]: `${K}${DotPrefix<DotNestedKeys<T[K]>>}` }[Exclude<
          keyof T,
          symbol
        >]
      : ''
) extends infer D
  ? Extract<D, string>
  : never

export type DotFormKeys<T> = DotNestedKeys<T>

export interface ProductDetails {
  campaignId?: string
  bundleId?: string
  counterOfferBundleId?: string
  prequalBundleId?: string
  prequalExperience?: string
  prequalPresentedOffers?: PresentedOffer[]
  presentedCounterOffers?: PresentedOffer[]
  featureFlags?: { [key: string]: boolean | string }
  // offerId to be submitted in direct app (i.e. bundle of 1 offer) or will exist in app summary after offer acceptance
  offerId?: string
  subChannelContextId?: string
  inviteCode?: string
  productCode?: string
}

export interface PresentedOffer {
  offerId: string
  rank: number
}

export interface AddOnProducts {
  creditProtectionProducts: CreditProtectionProduct[]
}

export const ThreeDsAuthState = {
  Pending: 'Pending',
  Success: 'Success',
  Failed: 'Failed',
} as const

export type ThreeDsAuthState = (typeof ThreeDsAuthState)[keyof typeof ThreeDsAuthState]


interface ProductAttributes {
  arq1?: string
  arq2?: string
  rewardsRate?: string
  depositAmount?: string
  initialCreditLine?: string
  earlySpend?: string
  categoryRewards?: CategoryReward
  categoryRewardsRate?: string
  overrideDuration?: string
  alternateDisclosureGroup?: string
  alternateDisclosureGroupMla?: string
}

export type OfferProduct = {
  /** Unique identifier for the product */
  id: string
  /** Product code */
  productCode: string
  /** Name of the product */
  name: string
  /** Attributes of the product as key-value pairs */
  attributes: ProductAttributes
  /** Attribute set identifier for the product, if applicable */
  attributeSetId?: string
  /** Identifier for the card art, if applicable */
  cardArt?: CardArtAssetName[]
}

export type OfferPricing = {
  /** Annual fee associated with the offer */
  annualFee: number
  /** Monthly fee (if any) associated with the offer */
  monthlyFee: number
  /** Cadence at which fee is assessed */
  feeFrequency: 'ANNUAL' | 'MONTHLY'
  /** Annual percentage rate (APR) for purchases adjusted by prime rate where appropriate */
  adjustedPurchaseApr: number
  /** Annual percentage rate (APR) for cash advances adjusted by prime rate where appropriate */
  adjustedCashApr: number
  /** Minimum credit line available with the offer */
  minimumLine: number
  /** Maximum credit line available with the offer, if applicable */
  maximumLine?: number
}

// OfferResponse mirrors offer-management-service's OfferResponse, with one rename: what offer-mgt
// calls `ProductDetails` is named `ProductDetailsV2` here. The `V2` suffix matches the other V2 wire
// types and avoids colliding with the existing `ProductDetails` (above) which, in CAFE / EAS /
// CC-APPS, is the application-submission data structure and is unrelated to offers.
//
// Nullability mirrors the Kotlin source: fields nullable upstream are typed `?: T | null` so
// consumers handle both an omitted key and an explicit JSON null.

export interface OfferResponse {
  /** Unique identifier for the offer */
  id: string
  /** Unique name of the offer */
  name: string
  /** Name of the sponsor bank for the offer */
  bank: string
  /** Product metadata incl. rewards / early-spend / secured attributes */
  product: ProductDetailsV2
  /** Offer data that directly informs the decisioning system */
  decisioningInput: DecisioningInput
  /** Card art available for selection with the offer */
  cardArtIds?: string[] | null
  /** Decisioning-only; write-only via the cc-applications mixin, so absent from responses. */
  creditPolicy?: string | null
  /** Decisioning-only; write-only via the cc-applications mixin, so absent from responses. */
  lineAssignmentPolicy?: string | null
  /** Name of the cashflow underwriting policy, if applicable */
  cashFlowUnderwritingPolicy?: string | null
  /** Standard pricing for the offer */
  standardRates: OfferRates
  /** Alternate (promotional) standard pricing for the offer */
  promotionalRates?: PromotionalRates | null
  /** Special military personnel pricing for the offer */
  mlaRates: OfferRates
  /** Alternate (promotional) military personnel pricing for the offer */
  promotionalMlaRates?: PromotionalRates | null
  /** The prime rate value intended for the offer pricing */
  primeRate?: number | null
  /** The effective date of the prime rate value */
  primeRateEffectiveDate?: string | null
  /** Decisioning-only; write-only via the cc-applications mixin, so absent from responses. */
  enabled?: boolean | null
}

export interface ProductDetailsV2 {
  /** Unique identifier for the product */
  id: string
  /** Attribute set identifier for the product */
  attributeSetId: string
  /** Name of the product */
  productName: string
  /** Product code */
  productCode: string
  /** Early-spend bonus attributes, if applicable */
  earlySpendAttributes?: EarlySpendAttributes | null
  /** Present only when the product carries a rewards program */
  rewardsAttributes?: RewardsAttributes | null
  /** Secured-card attributes, if applicable */
  securedAttributes?: SecuredAttributes | null
}

// Mirrors offer-mgt-service's com.missionlane.offermgt.repository.dto.offer.RewardsAttributes.
export interface RewardsAttributes {
  /** Rewards product code, e.g. "GEORGE_V1", "RWD_DYNAMIC" */
  rewardsProductCode: string
  /** Rewards program type discriminator */
  rewardsType: RewardsType
  /** Base rewards rate applied to non-category spend */
  baseRewardsRate: number
  /** Per-category rewards configuration */
  rewardsCategories: RewardsCategory[]
}

/** Rewards program type discriminator — mirrors offer-mgt RewardsType / cc-rewards RewardsProductType. */
export type RewardsType = 'STATIC' | 'DYNAMIC'

// Mirrors offer-mgt-service's RewardsCategory (less `rate`, which the frontend does not consume).
export interface RewardsCategory {
  /** Category name (e.g. GAS_V1, DINING_V1, DYNAMIC_V1) */
  name: string
  /** Human-readable category label (e.g. "Gas", "Top Spend Category"). Always present on the wire for
   * both STATIC and DYNAMIC categories. */
  displayName: string
  /** Effective rewards rate (total; for dynamic = baseRewardsRate + rate) */
  effectiveRate: number
  /** Rank for dynamic rewards (1 = top spender); absent for static */
  rank?: number | null
  /** Spend cap amount before the rate reverts to base, if any */
  spendCapAmount?: number | null
  /** Period over which the spend cap applies */
  spendCapPeriod?: SpendCapPeriod | null
}

export type SpendCapPeriod = 'YEARLY' | 'MONTHLY'

export interface EarlySpendAttributes {
  /** Bonus awarded for early spend */
  earlySpendBonus: number
}

export interface SecuredAttributes {
  /** Required deposit amount for the secured product */
  depositAmount: number
}

export interface OfferRates {
  /** Annual fee associated with the offer */
  annualFee: number
  /** Monthly fee (if any) associated with the offer */
  monthlyFee: number
  /** Cadence at which the fee is assessed */
  feeFrequency: FeeFrequency
  /** When the fee is first assessed relative to account lifecycle */
  feeTiming: FeeTiming
  /** APR for purchases adjusted by prime rate where appropriate */
  adjustedPurchaseApr: number
  /** APR for cash advances adjusted by prime rate where appropriate */
  adjustedCashApr: number
  /** Daily periodic rate for purchases */
  purchaseDailyPeriodicApr: number
  /** Daily periodic rate for cash advances */
  cashDailyPeriodicApr: number
}

export type FeeFrequency = 'MONTHLY' | 'ANNUAL'

export type FeeTiming = 'FIRST_TRANSACTION' | 'END_FIRST_CYCLE' | 'ACCOUNT_OPEN'

export interface PromotionalRates {
  /** Type of promotional rate */
  promotionalRateType: PromotionalRateType
  /** Duration of the promotional rate, in months */
  rateDurationMonths: number
  /** The promotional pricing */
  rates: OfferRates
}

export type PromotionalRateType = 'INTRO_RATE'

/** Mirrors offer-mgt DecisioningInputV1 */
export interface DecisioningInput {
  maxRiskGroup?: number | null
  minLine?: number | null
  maxLine?: number | null
  multiCard?: boolean | null
}

export type SubChannelAttribute = {
  /** Unique identifier for the sub-channel attribute */
  id: string
  /** Name of the attribute */
  attributeName: string
  /** Description of the attribute */
  description: string
}

export type SubChannelContext = {
  /** Unique identifier for the sub-channel context */
  id: string
  /** Name of the context within the sub-channel */
  contextName?: string
  /** Name of the channel */
  channel?: string
  /** Name of the sub-channel */
  subChannel?: string
  /** List of attributes associated with the sub-channel */
  subChannelAttributes?: SubChannelAttribute[]
}

// --- V2 wire types ---
// Mirror the offer-mgt-client (cc-applications V2 endpoints). These describe the raw payload as it
// comes off the wire; the V2 fetchers normalize them into the V1 shapes below at the seam, so no
// consumer above the network layer sees these. Names mirror the Kotlin source (as `OfferResponse`
// already does).

export interface ChannelTypeV1 {
  uuid: string
  channelTypeName: string
  description: string
}

export interface SubChannelV1 {
  uuid: string
  name: string
  description: string
}

export interface SubChannelAttributeV1 {
  uuid: string
  attributeName: string
  description: string
}

export interface SubChannelContextV1 {
  uuid: string
  contextName: string
  description: string
  channelType: ChannelTypeV1
  subChannel: SubChannelV1
  subChannelAttributes: SubChannelAttributeV1[]
}

export interface BundleOfferResponse {
  /** The offer included in the bundle */
  offer: OfferResponse
  /** Risk-group allocations; write-only via the cc-applications mixin, so absent from responses. */
  riskGroups?: Record<string, string>
}

export interface BundleResponse {
  id: string
  name: string
  offers: BundleOfferResponse[]
}

// offer-mgt's CampaignResponse, proxied through the cc-applications v2 endpoints.
export interface CampaignResponse {
  id: string
  name: string
  bundle: BundleResponse
  counterOfferBundle?: BundleResponse | null
  subChannelContext: SubChannelContextV1
  campaignAttributes?: string | null
}

// cc-applications wraps the proxied CampaignResponse and adds the DM campaign type.
export interface CampaignTermsResponseV2 {
  campaignResponse: CampaignResponse
  campaignType?: string | null
}

export interface ApplicationTermsResponseV2 {
  offer: OfferResponse
  subChannelContext: SubChannelContextV1
}

// Maps to offer-mgt's InviteCodeResponseV2 (cc-applications proxies it through unwrapped).
export interface InviteCodeTermsResponseV2 {
  inviteCode: string
  enabled: boolean
  campaign: CampaignResponse
}

export type CampaignType = ApplicationType

export type ApplicationTermsData = {
  disclosuresDoc: LegalDocument
  offerCagDoc: LegalDocument
  applicationTerms: ApplicationTerms
}

export type ApplicationTermsDataList = ApplicationTermsData[]

// TODO: changes are possible when the cc-applications endpoint is finalized.
// ─── Instant issuance eligibility ─────────────────────────────────────────────
// `eligibilities` is keyed by eligibility type; only the DIGITAL_WALLET_INSTANT_ISSUANCE
// entry and its top-level `eligible` are consumed.
export type InstantIssuanceEligibilityType = 'DIGITAL_WALLET_INSTANT_ISSUANCE'

export type InstantIssuanceReason = {
  pass: boolean
  code: string
  description: string
  checkFailed: boolean
}

export type InstantIssuanceEligibility = {
  eligible: boolean
  hasError: boolean
  reasons: InstantIssuanceReason[]
}

export type InstantIssuanceResponse = {
  eligibilities: Partial<Record<InstantIssuanceEligibilityType, InstantIssuanceEligibility>>
}
